import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAccessApi } from '../services/api';
import { useUserPermissions } from '../hooks/useUserPermissions';
import { useUserRoles } from '../hooks/useUserRoles';
import {
    useOrganizationTeams,
    useAddUserToTeamOrgAdmin,
    useRemoveUserFromTeamOrgAdmin,
    useUpdateUserTeamRoleOrgAdmin,
} from '../hooks/useApi';
import { ROLES } from '@wevo/wevo-access';
import { InlineSpinner, LoadingState } from '../components/LoadingSpinner';
import { OrganizationRolesEditor } from '../components/admin/OrganizationRolesEditor';
import { useRef, useState } from 'react';
import { Check, Lock, Plus, Star, User as UserIcon, UserMinus } from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Avatar } from '../components/ui/avatar';
import { Card } from '../components/ui/card';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { Actions, DialogFrame } from '../components/ui/dialog-frame';
import { InlineAlert } from '../components/ui/inline-alert';
import {
    ROLE_TOGGLE_ACTIVE_CLASS,
    ROLE_TOGGLE_BASE_CLASS,
    ROLE_TOGGLE_GROUP_CLASS,
    ROLE_TOGGLE_INACTIVE_CLASS,
} from '../components/admin/roleToggleStyles';
import {
    fieldLabelClassName,
    fieldValueClassName,
    overlineClassName,
    pageTitleClassName,
} from '../components/ui/typography';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { getProRoleName } from '../components/admin/teamRoleLabels';

/** Shapes for team_memberships on org-admin user detail (not yet on ApiClient return type) */
type OrgAdminTeamMembership = {
    team_role_id: string | number;
    Team: { id: string; name: string };
    TeamRole?: { name: string } | null;
};

type OrgAdminUserWithTeamMemberships = {
    team_memberships?: OrgAdminTeamMembership[];
};

type AdminUserTeam = {
    id: string;
    name: string;
    role: string;
    team_role_id: number;
};

const TEAM_ROLE_TOGGLES = [
    { roleId: 1, label: 'Restricted', title: 'Set as Pro Restricted' },
    { roleId: 2, label: 'User', title: 'Set as Pro User' },
    { roleId: 3, label: 'Approver', title: 'Set as Pro Approver' },
] as const;

function TeamRoleToggle({
    currentRoleId,
    roleId,
    label,
    title,
    pending,
    disabled,
    onClick,
}: {
    currentRoleId: number;
    roleId: number;
    label: string;
    title: string;
    pending: boolean;
    disabled: boolean;
    onClick: () => void;
}) {
    const isActive = Number(currentRoleId) === roleId;

    return (
        <button
            type="button"
            aria-pressed={isActive}
            aria-busy={pending ? true : undefined}
            disabled={disabled}
            onClick={onClick}
            className={cn(
                ROLE_TOGGLE_BASE_CLASS,
                isActive ? ROLE_TOGGLE_ACTIVE_CLASS : ROLE_TOGGLE_INACTIVE_CLASS,
            )}
            title={title}
        >
            {pending ? (
                <InlineSpinner className="size-3" label={null} />
            ) : (
                <Check className={cn('size-3 shrink-0', !isActive && 'invisible')} aria-hidden />
            )}
            {label}
        </button>
    );
}

export function AdminUserDetailPage() {
    const { userId, orgId } = useParams<{ userId: string; orgId: string }>();
    const { hasPermission, isLoading: permissionsLoading } = useUserPermissions();
    const { hasRole } = useUserRoles();
    const { getOrganizationUserById } = useAccessApi();
    const queryClient = useQueryClient();

    // Team management state
    const [showAddToTeamModal, setShowAddToTeamModal] = useState(false);
    const [addTeamError, setAddTeamError] = useState<string | null>(null);
    const addToTeamButtonRef = useRef<HTMLButtonElement>(null);
    const [showRemoveFromTeamModal, setShowRemoveFromTeamModal] = useState<{
        show: boolean;
        teamId?: string;
        teamName?: string;
    }>({ show: false });
    const [removeTeamError, setRemoveTeamError] = useState<string | null>(null);
    const [teamRoleError, setTeamRoleError] = useState<string | null>(null);
    // Check if current user is a developer
    const isDeveloper = hasRole(ROLES.DEVELOPER);

    // Query for detailed user data from Auth0 via organization-admin endpoint
    const {
        data: userData,
        isLoading: userLoading,
        error: userError,
        refetch: refetchUser,
        isFetching: isFetchingUser,
    } = useQuery({
        queryKey: ['adminUserDetail', orgId, userId],
        queryFn: async () => {
            if (!userId || !orgId) throw new Error('User ID and Organization ID are required');
            return getOrganizationUserById(orgId, userId);
        },
        enabled: !!userId && !!orgId && !permissionsLoading && hasPermission('administrate:users'),
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: 1,
        gcTime: 5 * 60 * 1000, // 5 minutes
        refetchOnWindowFocus: false,
    });
    useDocumentTitle(userData?.user?.name || 'User details');

    // Query for available teams (for adding user to teams)
    const { data: orgTeamsData } = useOrganizationTeams(orgId || null);

    // Team management mutations
    const addUserToTeam = useAddUserToTeamOrgAdmin();
    const removeUserFromTeam = useRemoveUserFromTeamOrgAdmin();
    const updateUserTeamRole = useUpdateUserTeamRoleOrgAdmin();

    // Use team_memberships from the updated API response
    const userWithTeamMemberships = userData?.user as OrgAdminUserWithTeamMemberships | undefined;
    const teams: AdminUserTeam[] =
        userWithTeamMemberships?.team_memberships
            ?.map((membership) => ({
                id: membership.Team.id,
                name: membership.Team.name,
                role: membership.TeamRole?.name || 'Basic',
                team_role_id: Number(membership.team_role_id),
            }))
            // Filter out empty teams (empty name represents company association, not a real team)
            .filter((team: { name: string }) => team.name && team.name.trim() !== '') || [];

    // Get available teams for adding user to (exclude teams user is already in)
    const availableTeams =
        orgTeamsData?.teams?.filter((team) => !teams.some((userTeam) => userTeam.id === team.id)) ||
        [];

    // Team management handlers
    const handleAddToTeam = async (teamId: string) => {
        if (!userId || !orgId) return;
        setAddTeamError(null);
        try {
            await addUserToTeam.mutateAsync({
                orgId,
                teamId,
                user_id: userId,
                team_role_id: 2, // Default to Pro User role
            });
            setShowAddToTeamModal(false);
            // Refresh user data
            queryClient.invalidateQueries({ queryKey: ['adminUserDetail', orgId, userId] });
        } catch (error) {
            console.error('Failed to add user to team:', error);
            setAddTeamError('Could not add this user to the team. Try again.');
        }
    };

    const handleRemoveFromTeam = async (teamId: string) => {
        if (!userId || !orgId) return;
        setRemoveTeamError(null);
        try {
            await removeUserFromTeam.mutateAsync({
                orgId,
                teamId,
                userId,
            });
            // Refresh user data
            await queryClient.invalidateQueries({
                queryKey: ['adminUserDetail', orgId, userId],
            });
            setShowRemoveFromTeamModal({ show: false });
        } catch (error) {
            console.error('Failed to remove user from team:', error);
            setRemoveTeamError('Could not remove this user from the team. Try again.');
        }
    };

    const handleSetTeamRole = async (teamId: string, newRoleId: number) => {
        if (!userId || !orgId) return;
        setTeamRoleError(null);
        try {
            await updateUserTeamRole.mutateAsync({
                orgId,
                teamId,
                userId,
                team_role_id: newRoleId,
            });
            // Refresh user data
            queryClient.invalidateQueries({ queryKey: ['adminUserDetail', orgId, userId] });
        } catch (error) {
            console.error('Failed to update user team role:', error);
            setTeamRoleError("Could not change this user's team role. Try again.");
        }
    };

    // Wait for permissions to load before making permission decisions
    if (permissionsLoading) {
        return (
            <Card>
                <LoadingState />
            </Card>
        );
    }

    if (!hasPermission('administrate:users')) {
        return (
            <Card className="overflow-hidden">
                <div
                    className="border-b border-border bg-muted/40 px-4 py-3"
                    style={{ minHeight: '42px' }}
                >
                    <div className="flex items-center gap-2">
                        <h1 className={overlineClassName}>Access denied</h1>
                    </div>
                </div>
                <div className="bg-white p-6">
                    <div role="alert" className="text-left text-sm text-neutral-600">
                        You don't have permission to view admin user details.
                    </div>
                </div>
            </Card>
        );
    }

    if (userLoading) {
        return (
            <Card>
                <LoadingState />
            </Card>
        );
    }

    if (userError) {
        return (
            <Card className="overflow-hidden">
                <div
                    className="border-b border-border bg-muted/40 px-4 py-3"
                    style={{ minHeight: '42px' }}
                >
                    <div className="flex items-center gap-2">
                        <h1 className={overlineClassName}>Couldn't load user</h1>
                    </div>
                </div>
                <div className="bg-white p-6">
                    <div role="alert" className="text-left text-sm text-red-600">
                        {(userError as Error)?.message ? (
                            <p className="mt-1 break-words text-neutral-500">
                                {(userError as Error).message}
                            </p>
                        ) : null}
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="mt-3"
                            onClick={() => void refetchUser()}
                            disabled={isFetchingUser}
                        >
                            {isFetchingUser ? 'Trying again…' : 'Try again'}
                        </Button>
                    </div>
                </div>
            </Card>
        );
    }

    if (!userId || !orgId || !userData) {
        return (
            <Card className="overflow-hidden">
                <div
                    className="border-b border-border bg-muted/40 px-4 py-3"
                    style={{ minHeight: '42px' }}
                >
                    <div className="flex items-center gap-2">
                        <h1 className={overlineClassName}>Not found</h1>
                    </div>
                </div>
                <div className="bg-white p-6">
                    <div role="alert" className="text-left text-sm text-red-600">
                        User not found.
                    </div>
                </div>
            </Card>
        );
    }

    const user = userData.user;

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    // Helper to get user initials
    const getUserInitials = (name: string) => {
        return name
            .split(' ')
            .map((n: string) => n[0])
            .join('')
            .toUpperCase();
    };

    return (
        <div className="space-y-6">
            {/* User Profile Card (not overflow-hidden, so the add-role menu can extend past it) */}
            <Card>
                {/* Header */}
                <div
                    className="rounded-t-xl border-b border-border bg-muted/40 px-4 py-3"
                    style={{ minHeight: '42px' }}
                >
                    <div className="flex items-center gap-2">
                        <p className={overlineClassName}>User profile</p>
                    </div>
                </div>

                {/* User Info Content */}
                <div className="bg-white p-6 rounded-b-xl">
                    <div className="flex items-center gap-4 mb-4">
                        <Avatar src={user.picture} initials={getUserInitials(user.name)} />

                        <div className="min-w-0">
                            <h1 className={cn(pageTitleClassName, 'mb-1')}>{user.name}</h1>
                            <p className="text-gray-600 mb-2 text-sm">{user.email}</p>
                        </div>
                    </div>
                    <div className="border-t border-gray-200 pt-4">
                        <OrganizationRolesEditor
                            orgId={orgId}
                            userId={userId}
                            userName={user.name}
                            roles={user.roles ?? []}
                            onRolesChange={(roles) => {
                                queryClient.setQueryData<typeof userData>(
                                    ['adminUserDetail', orgId, userId],
                                    (current) =>
                                        current
                                            ? { ...current, user: { ...current.user, roles } }
                                            : current,
                                );
                                // organization_admin changes also reset team roles shown below.
                                void queryClient.invalidateQueries({
                                    queryKey: ['adminUserDetail', orgId, userId],
                                });
                            }}
                        />
                    </div>
                </div>
            </Card>

            {/* Groups Card */}
            <Card className="overflow-hidden">
                {/* Header */}
                <div
                    className="border-b border-border bg-muted/40 px-4 py-3"
                    style={{ minHeight: '42px' }}
                >
                    <div className="flex items-center gap-2">
                        <h2 className={overlineClassName}>Team memberships</h2>
                        <span className="text-xs font-medium tabular-nums text-muted-foreground">
                            ({teams.length})
                        </span>
                        {hasPermission('manage:team_members') && (
                            <Button
                                ref={addToTeamButtonRef}
                                type="button"
                                variant="outline"
                                size="sm"
                                className="ml-auto"
                                onClick={() => {
                                    setAddTeamError(null);
                                    setShowAddToTeamModal(true);
                                }}
                                title="Add to team"
                            >
                                <Plus aria-hidden />
                                Add to team
                            </Button>
                        )}
                    </div>
                </div>

                {/* Content */}
                <div className="bg-white p-4">
                    {teamRoleError ? (
                        <InlineAlert tone="error" className="mb-3">
                            {teamRoleError}
                        </InlineAlert>
                    ) : null}
                    {!userLoading &&
                        !userError &&
                        (() => {
                            return teams.length === 0 ? (
                                <div className="text-center text-gray-500 py-4">
                                    User is not a member of any teams.
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {teams.map((team) => {
                                        const isTeamRolePending =
                                            updateUserTeamRole.isPending &&
                                            updateUserTeamRole.variables?.teamId === team.id;
                                        return (
                                            <div key={team.id} className="rounded-lg border p-4">
                                                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                                    <div className="min-w-0 flex-1">
                                                        <div className="mb-1 flex flex-wrap items-center gap-2">
                                                            <h3 className="min-w-0 break-words text-sm font-semibold text-neutral-950">
                                                                {team.name}
                                                            </h3>
                                                            {team.team_role_id === 1 && (
                                                                <span className="inline-flex items-center gap-1 whitespace-nowrap px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                                                    <Lock
                                                                        className="size-3 shrink-0"
                                                                        aria-hidden
                                                                    />
                                                                    Restricted
                                                                </span>
                                                            )}
                                                            {team.team_role_id === 2 && (
                                                                <span className="inline-flex items-center gap-1 whitespace-nowrap px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                                    <UserIcon
                                                                        className="size-3 shrink-0"
                                                                        aria-hidden
                                                                    />
                                                                    User
                                                                </span>
                                                            )}
                                                            {team.team_role_id === 3 && (
                                                                <span className="inline-flex items-center gap-1 whitespace-nowrap px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                                                    <Star
                                                                        className="size-3 shrink-0"
                                                                        aria-hidden
                                                                    />
                                                                    Approver
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="text-xs text-gray-500">
                                                            <span className="font-medium">
                                                                Role:
                                                            </span>{' '}
                                                            <span>
                                                                {getProRoleName(
                                                                    team.team_role_id,
                                                                    team.role,
                                                                )}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-wrap items-center gap-1 sm:shrink-0">
                                                        <div
                                                            role="group"
                                                            aria-label={`Role in ${team.name}`}
                                                            className={ROLE_TOGGLE_GROUP_CLASS}
                                                        >
                                                            {TEAM_ROLE_TOGGLES.map((roleToggle) => (
                                                                <TeamRoleToggle
                                                                    key={roleToggle.roleId}
                                                                    currentRoleId={
                                                                        team.team_role_id
                                                                    }
                                                                    roleId={roleToggle.roleId}
                                                                    label={roleToggle.label}
                                                                    title={roleToggle.title}
                                                                    disabled={isTeamRolePending}
                                                                    pending={
                                                                        isTeamRolePending &&
                                                                        updateUserTeamRole.variables
                                                                            ?.team_role_id ===
                                                                            roleToggle.roleId
                                                                    }
                                                                    onClick={() =>
                                                                        handleSetTeamRole(
                                                                            team.id,
                                                                            roleToggle.roleId,
                                                                        )
                                                                    }
                                                                />
                                                            ))}
                                                        </div>
                                                        <Button
                                                            type="button"
                                                            variant="destructiveOutline"
                                                            size="sm"
                                                            className="ml-2"
                                                            aria-label={`Remove from ${team.name}`}
                                                            onClick={() => {
                                                                setRemoveTeamError(null);
                                                                setShowRemoveFromTeamModal({
                                                                    show: true,
                                                                    teamId: team.id,
                                                                    teamName: team.name,
                                                                });
                                                            }}
                                                            disabled={teams.length <= 1}
                                                            title={
                                                                teams.length <= 1
                                                                    ? 'Cannot remove user from their only team'
                                                                    : 'Remove from team'
                                                            }
                                                        >
                                                            <UserMinus aria-hidden />
                                                            Remove
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })()}
                </div>
            </Card>

            {/* Information Cards */}
            <div className="grid md:grid-cols-2 gap-6">
                {/* Basic Information Card */}
                <Card className="overflow-hidden">
                    {/* Header */}
                    <div
                        className="border-b border-border bg-muted/40 px-4 py-3"
                        style={{ minHeight: '42px' }}
                    >
                        <div className="flex items-center gap-2">
                            <h2 className={overlineClassName}>Basic information</h2>
                        </div>
                    </div>

                    {/* Content */}
                    <dl className="bg-white p-4 space-y-4">
                        <div>
                            <dt className={fieldLabelClassName}>User ID</dt>
                            <dd className="mt-1 break-all font-mono text-sm text-neutral-950">
                                {user.user_id}
                            </dd>
                        </div>

                        <div>
                            <dt className={fieldLabelClassName}>Organization ID</dt>
                            <dd className="mt-1 break-all font-mono text-sm text-neutral-950">
                                {user.organization_id}
                            </dd>
                        </div>

                        <div>
                            <dt className={fieldLabelClassName}>Email</dt>
                            <dd className={fieldValueClassName}>{user.email}</dd>
                        </div>

                        <div>
                            <dt className={fieldLabelClassName}>Full name</dt>
                            <dd className={fieldValueClassName}>
                                {user.given_name && user.family_name
                                    ? `${user.given_name} ${user.family_name}`
                                    : user.name}
                            </dd>
                        </div>

                        {user.nickname && (
                            <div>
                                <dt className={fieldLabelClassName}>Nickname</dt>
                                <dd className={fieldValueClassName}>{user.nickname}</dd>
                            </div>
                        )}
                    </dl>
                </Card>

                {/* Authentication & Access Card */}
                <Card className="overflow-hidden">
                    {/* Header */}
                    <div
                        className="border-b border-border bg-muted/40 px-4 py-3"
                        style={{ minHeight: '42px' }}
                    >
                        <div className="flex items-center gap-2">
                            <h2 className={overlineClassName}>Authentication & access</h2>
                        </div>
                    </div>

                    {/* Content */}
                    <dl className="bg-white p-4 space-y-4">
                        <div>
                            <dt className={fieldLabelClassName}>Email verified</dt>
                            <dd
                                className={`mt-1 text-sm font-medium ${user.email_verified ? 'text-green-700' : 'text-red-700'}`}
                            >
                                {user.email_verified ? 'Verified' : 'Not verified'}
                            </dd>
                        </div>

                        <div>
                            <dt className={fieldLabelClassName}>Login count</dt>
                            <dd className={cn(fieldValueClassName, 'tabular-nums')}>
                                {user.login_count}
                            </dd>
                        </div>

                        <div>
                            <dt className={fieldLabelClassName}>Account created</dt>
                            <dd className={fieldValueClassName}>{formatDate(user.created_at)}</dd>
                        </div>

                        <div>
                            <dt className={fieldLabelClassName}>Last updated</dt>
                            <dd className={fieldValueClassName}>{formatDate(user.updated_at)}</dd>
                        </div>
                    </dl>
                </Card>
            </div>

            {/* User Metadata */}
            {isDeveloper &&
                (Object.keys(user.user_metadata).length > 0 ||
                    Object.keys(user.app_metadata).length > 0) && (
                    <Card className="overflow-hidden">
                        {/* Header */}
                        <div
                            className="border-b border-border bg-muted/40 px-4 py-3"
                            style={{ minHeight: '42px' }}
                        >
                            <div className="flex items-center gap-2">
                                <h2 className={overlineClassName}>Metadata</h2>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="bg-white p-4">
                            <div className="grid md:grid-cols-2 gap-6">
                                {Object.keys(user.user_metadata).length > 0 && (
                                    <div>
                                        <h3 className="mb-3 text-sm font-semibold text-neutral-950">
                                            User metadata
                                        </h3>
                                        <div className="bg-gray-50 rounded-lg p-4">
                                            <pre className="text-xs text-gray-700 whitespace-pre-wrap">
                                                {JSON.stringify(user.user_metadata, null, 2)}
                                            </pre>
                                        </div>
                                    </div>
                                )}

                                {Object.keys(user.app_metadata).length > 0 && (
                                    <div>
                                        <h3 className="mb-3 text-sm font-semibold text-neutral-950">
                                            App metadata
                                        </h3>
                                        <div className="bg-gray-50 rounded-lg p-4">
                                            <pre className="text-xs text-gray-700 whitespace-pre-wrap">
                                                {JSON.stringify(user.app_metadata, null, 2)}
                                            </pre>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Card>
                )}

            {/* Identity Providers */}
            {isDeveloper && user.identities && user.identities.length > 0 && (
                <Card className="overflow-hidden">
                    {/* Header */}
                    <div
                        className="border-b border-border bg-muted/40 px-4 py-3"
                        style={{ minHeight: '42px' }}
                    >
                        <div className="flex items-center gap-2">
                            <h2 className={overlineClassName}>Identity providers</h2>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="bg-white p-4">
                        <div className="space-y-2">
                            {user.identities.map(
                                (identity: Record<string, unknown>, index: number) => (
                                    <div
                                        key={index}
                                        className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 rounded-md bg-gray-50 px-3 py-2 text-sm"
                                    >
                                        <span className="font-medium text-neutral-950">
                                            {(identity.provider as string) || 'Unknown'}
                                        </span>
                                        <span className="text-gray-600">
                                            Connected: {(identity.connection as string) || 'N/A'}
                                        </span>
                                    </div>
                                ),
                            )}
                        </div>
                    </div>
                </Card>
            )}

            <DialogFrame
                open={showAddToTeamModal}
                title="Add user to team"
                description="Select a team to add this user to."
                onClose={() => setShowAddToTeamModal(false)}
                panelClassName="max-w-md"
            >
                {addTeamError ? (
                    <InlineAlert tone="error" className="mb-3">
                        {addTeamError}
                    </InlineAlert>
                ) : null}
                <div className="space-y-3">
                    {availableTeams.map((team) => (
                        <Button
                            key={team.id}
                            type="button"
                            variant="outline"
                            className="w-full justify-between"
                            loading={
                                addUserToTeam.isPending &&
                                addUserToTeam.variables?.teamId === team.id
                            }
                            disabled={addUserToTeam.isPending}
                            onClick={() => handleAddToTeam(team.id)}
                        >
                            {team.name}
                            <Plus aria-hidden />
                        </Button>
                    ))}
                </div>
                <Actions onCancel={() => setShowAddToTeamModal(false)} />
            </DialogFrame>

            <ConfirmDialog
                open={showRemoveFromTeamModal.show}
                title="Remove user from team"
                description={
                    <>
                        Are you sure you want to remove this user from{' '}
                        <span className="font-semibold">{showRemoveFromTeamModal.teamName}</span>
                        {'?'}
                    </>
                }
                confirmLabel="Remove"
                destructive
                error={removeTeamError}
                returnFocusRef={addToTeamButtonRef}
                onCancel={() => {
                    setRemoveTeamError(null);
                    setShowRemoveFromTeamModal({ show: false });
                }}
                onConfirm={() => handleRemoveFromTeam(showRemoveFromTeamModal.teamId!)}
            />
        </div>
    );
}
